/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetAdminPlatformMarketsRow, GetAdminTreasuryWalletsRow, GetAdminTreasurySolvencyRow, GetPartnerCreatorsRow, GetPartnerCreatorRow, FindPartnerAccountsRow, GetAdminCompetitionsRow, GetAdminCompetitionRow, GetAdminCompetitionResultsRow, GetAdminCompetitionAuditRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String } from '../../helpers/index.js';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetAdminPlatformMarkets ────────────────────────────────────────────────────────────

export interface useGetAdminPlatformMarketsArgs { readonly limit?: string; readonly cursor?: string; }


async function _useGetAdminPlatformMarketsQueryFn(
  args: useGetAdminPlatformMarketsArgs,
  ctx: QueryFunctionContext,
): Promise<GetAdminPlatformMarketsRow | null> {
  const url = '/v1/admin/platform-markets';
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  if (args.cursor !== undefined) params.set('cursor', String(args.cursor));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminPlatformMarkets',
    routeTemplate: '/v1/admin/platform-markets',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetAdminPlatformMarketsRow;
}


const _useGetAdminPlatformMarketsFactory = Object.assign(
  (args: useGetAdminPlatformMarketsArgs) => ({
    queryKey: ['admin', 'platform-markets', { limit: args.limit ?? null, cursor: args.cursor ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminPlatformMarketsQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminPlatformMarkets',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['admin', 'platform-markets'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminPlatformMarketsArgs, GetAdminPlatformMarketsRow>;


export const useGetAdminPlatformMarketsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/platform-markets',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetAdminPlatformMarketsFactory },
  parseInput: (raw: unknown): useGetAdminPlatformMarketsArgs => raw as useGetAdminPlatformMarketsArgs,
  parseResponse: (raw: unknown): GetAdminPlatformMarketsRow => raw as GetAdminPlatformMarketsRow,
  specName: 'useGetAdminPlatformMarkets' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetAdminPlatformMarketsRow>;


// ─── useGetAdminTreasuryWallets ────────────────────────────────────────────────────────────

export interface useGetAdminTreasuryWalletsArgs {  }


async function _useGetAdminTreasuryWalletsQueryFn(
  args: useGetAdminTreasuryWalletsArgs,
  ctx: QueryFunctionContext,
): Promise<GetAdminTreasuryWalletsRow | null> {
  const url = '/v1/admin/treasury/wallets';
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminTreasuryWallets',
    routeTemplate: '/v1/admin/treasury/wallets',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetAdminTreasuryWalletsRow;
}


const _useGetAdminTreasuryWalletsFactory = Object.assign(
  (args: useGetAdminTreasuryWalletsArgs) => ({
    queryKey: ['admin', 'treasury-wallets'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminTreasuryWalletsQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminTreasuryWallets',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['admin', 'treasury-wallets'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminTreasuryWalletsArgs, GetAdminTreasuryWalletsRow>;


export const useGetAdminTreasuryWalletsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/treasury/wallets',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetAdminTreasuryWalletsFactory },
  parseInput: (raw: unknown): useGetAdminTreasuryWalletsArgs => raw as useGetAdminTreasuryWalletsArgs,
  parseResponse: (raw: unknown): GetAdminTreasuryWalletsRow => raw as GetAdminTreasuryWalletsRow,
  specName: 'useGetAdminTreasuryWallets' as const,
  polling: { hasNats: false, intervalMs: 30000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetAdminTreasuryWalletsRow>;


// ─── useGetAdminTreasurySolvency ────────────────────────────────────────────────────────────

export interface useGetAdminTreasurySolvencyArgs { readonly limit?: string; }


async function _useGetAdminTreasurySolvencyQueryFn(
  args: useGetAdminTreasurySolvencyArgs,
  ctx: QueryFunctionContext,
): Promise<GetAdminTreasurySolvencyRow | null> {
  const url = '/v1/admin/treasury/solvency';
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  if (args.limit !== undefined) params.set('limit', String(args.limit));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminTreasurySolvency',
    routeTemplate: '/v1/admin/treasury/solvency',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetAdminTreasurySolvencyRow;
}


const _useGetAdminTreasurySolvencyFactory = Object.assign(
  (args: useGetAdminTreasurySolvencyArgs) => ({
    queryKey: ['admin', 'treasury-solvency', { limit: args.limit ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminTreasurySolvencyQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminTreasurySolvency',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['admin', 'treasury-solvency'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminTreasurySolvencyArgs, GetAdminTreasurySolvencyRow>;


export const useGetAdminTreasurySolvencySpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/treasury/solvency',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetAdminTreasurySolvencyFactory },
  parseInput: (raw: unknown): useGetAdminTreasurySolvencyArgs => raw as useGetAdminTreasurySolvencyArgs,
  parseResponse: (raw: unknown): GetAdminTreasurySolvencyRow => raw as GetAdminTreasurySolvencyRow,
  specName: 'useGetAdminTreasurySolvency' as const,
  polling: { hasNats: false, intervalMs: 120000 } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetAdminTreasurySolvencyRow>;


// ─── useGetPartnerCreators ────────────────────────────────────────────────────────────

export interface useGetPartnerCreatorsArgs { readonly viewportGated?: boolean; }


async function _useGetPartnerCreatorsQueryFn(
  args: useGetPartnerCreatorsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetPartnerCreatorsRow>> {
  const url = '/v1/admin/creators';
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 25));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetPartnerCreators',
    routeTemplate: '/v1/admin/creators',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetPartnerCreatorsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetPartnerCreatorsFactory = Object.assign(
  (args: useGetPartnerCreatorsArgs) => ({
    queryKey: ['admin', 'partner-creators'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetPartnerCreatorsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetPartnerCreators',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['admin', 'partner-creators'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetPartnerCreatorsArgs, PagedData<GetPartnerCreatorsRow>>;


export const useGetPartnerCreatorsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/creators',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetPartnerCreatorsFactory },
  parseInput: (raw: unknown): useGetPartnerCreatorsArgs => raw as useGetPartnerCreatorsArgs,
  parseResponse: (raw: unknown): PagedData<GetPartnerCreatorsRow> => raw as PagedData<GetPartnerCreatorsRow>,
  specName: 'useGetPartnerCreators' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['partner-creator-admin', 'viewer', 'unbound', id] as readonly unknown[],
  entityStore: { resource: 'partner-creator-admin' } as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetPartnerCreatorsRow>>;
export const useGetPartnerCreatorsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 25,
  maxPageSize: 25,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetPartnerCreatorsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetPartnerCreatorsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetPartnerCreatorsPerEntitySubjects(_args: useGetPartnerCreatorsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetPartnerCreatorsRowSubjectTemplates(_args: useGetPartnerCreatorsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetPartnerCreatorsApplyEvent(
  prev: PagedData<GetPartnerCreatorsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetPartnerCreatorsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetPartnerCreatorsRealtime = {
  domain: 'admin' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetPartnerCreatorsArgs) => _useGetPartnerCreatorsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetPartnerCreatorsArgs) => _useGetPartnerCreatorsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetPartnerCreatorsArgs) =>
    (prev: PagedData<GetPartnerCreatorsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetPartnerCreatorsRow> | undefined =>
      _useGetPartnerCreatorsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetPartnerCreatorsRow>, unknown>;


// ─── useGetPartnerCreator ────────────────────────────────────────────────────────────

export interface useGetPartnerCreatorArgs { readonly id: string; }


async function _useGetPartnerCreatorQueryFn(
  args: useGetPartnerCreatorArgs,
  ctx: QueryFunctionContext,
): Promise<GetPartnerCreatorRow | null> {
  if (args.id === undefined || args.id === null || args.id === '') return null;
  const url = `/v1/admin/creators/${args.id}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetPartnerCreator',
    routeTemplate: '/v1/admin/creators/{id}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetPartnerCreatorRow;
}


const _useGetPartnerCreatorFactory = Object.assign(
  (args: useGetPartnerCreatorArgs) => ({
    queryKey: ['admin', 'partner-creator', args.id] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetPartnerCreatorQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetPartnerCreator',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['admin', 'partner-creator', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetPartnerCreatorArgs, GetPartnerCreatorRow>;


export const useGetPartnerCreatorSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/creators/{id}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetPartnerCreatorFactory },
  parseInput: (raw: unknown): useGetPartnerCreatorArgs => raw as useGetPartnerCreatorArgs,
  parseResponse: (raw: unknown): GetPartnerCreatorRow => raw as GetPartnerCreatorRow,
  specName: 'useGetPartnerCreator' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetPartnerCreatorRow>;


// ─── useFindPartnerAccounts ────────────────────────────────────────────────────────────

export interface useFindPartnerAccountsArgs { readonly query?: string; readonly viewportGated?: boolean; }


async function _useFindPartnerAccountsQueryFn(
  args: useFindPartnerAccountsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<FindPartnerAccountsRow>> {
  const url = '/v1/admin/creators/accounts';
  const params = new URLSearchParams();

  if (args.query !== undefined) params.set('query', String(args.query));
  params.set('limit', String(limitOverride ?? 25));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useFindPartnerAccounts',
    routeTemplate: '/v1/admin/creators/accounts',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly FindPartnerAccountsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useFindPartnerAccountsFactory = Object.assign(
  (args: useFindPartnerAccountsArgs) => ({
    queryKey: ['admin', 'partner-account-search', { query: args.query ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useFindPartnerAccountsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useFindPartnerAccounts',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['admin', 'partner-account-search'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useFindPartnerAccountsArgs, PagedData<FindPartnerAccountsRow>>;


export const useFindPartnerAccountsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/creators/accounts',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useFindPartnerAccountsFactory },
  parseInput: (raw: unknown): useFindPartnerAccountsArgs => raw as useFindPartnerAccountsArgs,
  parseResponse: (raw: unknown): PagedData<FindPartnerAccountsRow> => raw as PagedData<FindPartnerAccountsRow>,
  specName: 'useFindPartnerAccounts' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<FindPartnerAccountsRow>>;
export const useFindPartnerAccountsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 25,
  maxPageSize: 25,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useFindPartnerAccountsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useFindPartnerAccountsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useFindPartnerAccountsPerEntitySubjects(_args: useFindPartnerAccountsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useFindPartnerAccountsRowSubjectTemplates(_args: useFindPartnerAccountsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useFindPartnerAccountsApplyEvent(
  prev: PagedData<FindPartnerAccountsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<FindPartnerAccountsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useFindPartnerAccountsRealtime = {
  domain: 'admin' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useFindPartnerAccountsArgs) => _useFindPartnerAccountsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useFindPartnerAccountsArgs) => _useFindPartnerAccountsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useFindPartnerAccountsArgs) =>
    (prev: PagedData<FindPartnerAccountsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<FindPartnerAccountsRow> | undefined =>
      _useFindPartnerAccountsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<FindPartnerAccountsRow>, unknown>;


// ─── useGetAdminCompetitions ────────────────────────────────────────────────────────────

export interface useGetAdminCompetitionsArgs { readonly viewportGated?: boolean; }


async function _useGetAdminCompetitionsQueryFn(
  args: useGetAdminCompetitionsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetAdminCompetitionsRow>> {
  const url = '/v1/admin/competitions';
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 100));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminCompetitions',
    routeTemplate: '/v1/admin/competitions',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetAdminCompetitionsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetAdminCompetitionsFactory = Object.assign(
  (args: useGetAdminCompetitionsArgs) => ({
    queryKey: ['admin', 'list'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminCompetitionsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminCompetitions',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['admin', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminCompetitionsArgs, PagedData<GetAdminCompetitionsRow>>;


export const useGetAdminCompetitionsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/competitions',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetAdminCompetitionsFactory },
  parseInput: (raw: unknown): useGetAdminCompetitionsArgs => raw as useGetAdminCompetitionsArgs,
  parseResponse: (raw: unknown): PagedData<GetAdminCompetitionsRow> => raw as PagedData<GetAdminCompetitionsRow>,
  specName: 'useGetAdminCompetitions' as const,
  polling: { hasNats: false, intervalMs: 60000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['competitions', 'entity', id] as readonly unknown[],
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetAdminCompetitionsRow>>;
export const useGetAdminCompetitionsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 100,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetAdminCompetitionsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetAdminCompetitionsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetAdminCompetitionsPerEntitySubjects(_args: useGetAdminCompetitionsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetAdminCompetitionsRowSubjectTemplates(_args: useGetAdminCompetitionsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetAdminCompetitionsApplyEvent(
  prev: PagedData<GetAdminCompetitionsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetAdminCompetitionsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetAdminCompetitionsRealtime = {
  domain: 'admin' as const,
  domainPins: [] as readonly string[],
  rowKey: 'id' as const,
  perEntitySubjects: (args: useGetAdminCompetitionsArgs) => _useGetAdminCompetitionsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetAdminCompetitionsArgs) => _useGetAdminCompetitionsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetAdminCompetitionsArgs) =>
    (prev: PagedData<GetAdminCompetitionsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetAdminCompetitionsRow> | undefined =>
      _useGetAdminCompetitionsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetAdminCompetitionsRow>, unknown>;


// ─── useGetAdminCompetition ────────────────────────────────────────────────────────────

export interface useGetAdminCompetitionArgs { readonly competitionId: string; }


async function _useGetAdminCompetitionQueryFn(
  args: useGetAdminCompetitionArgs,
  ctx: QueryFunctionContext,
): Promise<GetAdminCompetitionRow | null> {
  if (args.competitionId === undefined || args.competitionId === null || args.competitionId === '') return null;
  const url = `/v1/admin/competitions/${args.competitionId}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminCompetition',
    routeTemplate: '/v1/admin/competitions/{competitionId}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetAdminCompetitionRow;
}


const _useGetAdminCompetitionFactory = Object.assign(
  (args: useGetAdminCompetitionArgs) => ({
    queryKey: ['admin', 'detail', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminCompetitionQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminCompetition',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['admin', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminCompetitionArgs, GetAdminCompetitionRow>;


export const useGetAdminCompetitionSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/competitions/{competitionId}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetAdminCompetitionFactory },
  parseInput: (raw: unknown): useGetAdminCompetitionArgs => raw as useGetAdminCompetitionArgs,
  parseResponse: (raw: unknown): GetAdminCompetitionRow => raw as GetAdminCompetitionRow,
  specName: 'useGetAdminCompetition' as const,
  polling: { hasNats: false, intervalMs: 60000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetAdminCompetitionRow>;


// ─── useGetAdminCompetitionResults ────────────────────────────────────────────────────────────

export interface useGetAdminCompetitionResultsArgs { readonly competitionId: string; readonly viewportGated?: boolean; }


async function _useGetAdminCompetitionResultsQueryFn(
  args: useGetAdminCompetitionResultsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetAdminCompetitionResultsRow>> {
  const url = `/v1/admin/competitions/${args.competitionId}/results`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminCompetitionResults',
    routeTemplate: '/v1/admin/competitions/{competitionId}/results',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetAdminCompetitionResultsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetAdminCompetitionResultsFactory = Object.assign(
  (args: useGetAdminCompetitionResultsArgs) => ({
    queryKey: ['admin', 'competition-results', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminCompetitionResultsQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminCompetitionResults',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['admin', 'competition-results', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminCompetitionResultsArgs, PagedData<GetAdminCompetitionResultsRow>>;


export const useGetAdminCompetitionResultsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/competitions/{competitionId}/results',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetAdminCompetitionResultsFactory },
  parseInput: (raw: unknown): useGetAdminCompetitionResultsArgs => raw as useGetAdminCompetitionResultsArgs,
  parseResponse: (raw: unknown): PagedData<GetAdminCompetitionResultsRow> => raw as PagedData<GetAdminCompetitionResultsRow>,
  specName: 'useGetAdminCompetitionResults' as const,
  polling: { hasNats: false, intervalMs: 30000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetAdminCompetitionResultsRow>>;
export const useGetAdminCompetitionResultsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetAdminCompetitionResultsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetAdminCompetitionResultsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetAdminCompetitionResultsPerEntitySubjects(_args: useGetAdminCompetitionResultsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetAdminCompetitionResultsRowSubjectTemplates(_args: useGetAdminCompetitionResultsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetAdminCompetitionResultsApplyEvent(
  prev: PagedData<GetAdminCompetitionResultsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetAdminCompetitionResultsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetAdminCompetitionResultsRealtime = {
  domain: 'admin' as const,
  domainPins: [] as readonly string[],
  rowKey: 'rank' as const,
  perEntitySubjects: (args: useGetAdminCompetitionResultsArgs) => _useGetAdminCompetitionResultsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetAdminCompetitionResultsArgs) => _useGetAdminCompetitionResultsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetAdminCompetitionResultsArgs) =>
    (prev: PagedData<GetAdminCompetitionResultsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetAdminCompetitionResultsRow> | undefined =>
      _useGetAdminCompetitionResultsApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetAdminCompetitionResultsRow>, unknown>;


// ─── useGetAdminCompetitionAudit ────────────────────────────────────────────────────────────

export interface useGetAdminCompetitionAuditArgs { readonly competitionId: string; readonly viewportGated?: boolean; }


async function _useGetAdminCompetitionAuditQueryFn(
  args: useGetAdminCompetitionAuditArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetAdminCompetitionAuditRow>> {
  const url = `/v1/admin/competitions/${args.competitionId}/audit`;
  const params = new URLSearchParams();

  params.set('limit', String(limitOverride ?? 20));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAdminCompetitionAudit',
    routeTemplate: '/v1/admin/competitions/{competitionId}/audit',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetAdminCompetitionAuditRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetAdminCompetitionAuditFactory = Object.assign(
  (args: useGetAdminCompetitionAuditArgs) => ({
    queryKey: ['admin', 'competition-audit', args.competitionId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAdminCompetitionAuditQueryFn(args, ctx),
    staleTime: 30000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAdminCompetitionAudit',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['admin', 'competition-audit', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAdminCompetitionAuditArgs, PagedData<GetAdminCompetitionAuditRow>>;


export const useGetAdminCompetitionAuditSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/admin/competitions/{competitionId}/audit',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { list: _useGetAdminCompetitionAuditFactory },
  parseInput: (raw: unknown): useGetAdminCompetitionAuditArgs => raw as useGetAdminCompetitionAuditArgs,
  parseResponse: (raw: unknown): PagedData<GetAdminCompetitionAuditRow> => raw as PagedData<GetAdminCompetitionAuditRow>,
  specName: 'useGetAdminCompetitionAudit' as const,
  polling: { hasNats: false, intervalMs: 30000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetAdminCompetitionAuditRow>>;
export const useGetAdminCompetitionAuditPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 20,
  maxPageSize: 20,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetAdminCompetitionAuditArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetAdminCompetitionAuditQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetAdminCompetitionAuditPerEntitySubjects(_args: useGetAdminCompetitionAuditArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = [];
  return [...new Set(_subs)];
}

function _useGetAdminCompetitionAuditRowSubjectTemplates(_args: useGetAdminCompetitionAuditArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetAdminCompetitionAuditApplyEvent(
  prev: PagedData<GetAdminCompetitionAuditRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetAdminCompetitionAuditRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}

export const useGetAdminCompetitionAuditRealtime = {
  domain: 'admin' as const,
  domainPins: [] as readonly string[],
  rowKey: 'at' as const,
  perEntitySubjects: (args: useGetAdminCompetitionAuditArgs) => _useGetAdminCompetitionAuditPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetAdminCompetitionAuditArgs) => _useGetAdminCompetitionAuditRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetAdminCompetitionAuditArgs) =>
    (prev: PagedData<GetAdminCompetitionAuditRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetAdminCompetitionAuditRow> | undefined =>
      _useGetAdminCompetitionAuditApplyEvent(prev, ev),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetAdminCompetitionAuditRow>, unknown>;
